import { useSuspenseQuery } from '@tanstack/react-query';
import { use, useEffect, useState } from 'react';

import { SiteContext } from 'src/context/site-context';
import { DigitalSignatureLevel } from 'src/enums/digital-signature-level';
import { NoMembershipsAvailableMessage } from 'src/enums/no-memberships-available-message';

import { get } from './request-helper';

type BooleanFlagType = 'boolean';
enum LdFeatureFlagType {
  NUMBER = 'number',
  STRING = 'string',
}

type KindOf<T> = [T] extends [boolean]
  ? BooleanFlagType
  : [T] extends [number]
    ? LdFeatureFlagType.NUMBER
    : [T] extends [string]
      ? LdFeatureFlagType.STRING
      : never;

export type FeatureFlag<T> = {
  key: string;
  kind: KindOf<T>;
  defaultValue: T;
};

export const defineBooleanFlag = (key: string, defaultValue = false): FeatureFlag<boolean> => ({
  key,
  kind: 'boolean',
  defaultValue,
});

export const defineNumberFlag = (key: string, defaultValue: number): FeatureFlag<number> => ({
  key,
  kind: LdFeatureFlagType.NUMBER,
  defaultValue,
});

export const defineStringFlag = <T extends string>(key: string, defaultValue: T): FeatureFlag<T> =>
  ({
    key,
    kind: LdFeatureFlagType.STRING,
    defaultValue,
  }) as FeatureFlag<T>;

export const FEATURE_FLAG = {
  PUZZEL_LIVE_CHAT_INSTEAD_OF_TAWK: defineBooleanFlag('gatsby-puzzel-live-chat-instead-of-tawk'),
  ENABLE_CLUB_FACILITIES_MOBILE_NAV: defineBooleanFlag('gatsby-enable-club-facilities-mobile-nav'),
  COLLECT_KICKSTART_ANSWERS: defineBooleanFlag('collect-kickstart-answers-in-online-joining'),
  ALLOW_PAY_BY_LINK_WHEN_JOINING_IN_CLUB: defineBooleanFlag(
    'allow-pay-by-link-when-joining-in-club'
  ),
  SHOW_PRICE_INCREASE_WARNING_WHEN_JOINING: defineBooleanFlag(
    'show-price-increase-warning-when-joining'
  ),
  SHOW_TELEPHONE_MARKETING_CONSENT: defineBooleanFlag('show-telephone-marketing-consent'),
  SHOW_TELEPHONE_MARKETING_CONSENT_ONLINE_JOINING: defineBooleanFlag(
    'show-telephone-marketing-consent-online-joining'
  ),
  SHOW_WHATS_APP_MARKETING_CONSENT_ONLINE_JOINING: defineBooleanFlag(
    'show-whats-app-marketing-consent-online-joining'
  ),
  SHOW_WHATS_APP_MARKETING_CONSENT_SALES_APP: defineBooleanFlag(
    'show-whats-app-marketing-consent-sales-app'
  ),
  SHOW_WHATS_APP_MARKETING_CONSENT_ENQUIRY: defineBooleanFlag(
    'show-whats-app-marketing-consent-enquiry'
  ),
  SHOW_WHATS_APP_MARKETING_CONSENT_TOUR: defineBooleanFlag('show-whats-app-marketing-consent-tour'),
  SHOW_WHATS_APP_MARKETING_CONSENT_TRIAL: defineBooleanFlag(
    'show-whats-app-marketing-consent-trial'
  ),
  CAPTURE_POSTCODE_ON_LETS_GET_STARTED_PAGE: defineBooleanFlag(
    'capture-postcode-on-online-joining-lets-get-started-page'
  ),
  ENABLE_IDEAL_PAYMENTS: defineBooleanFlag('enable-ideal-payments'),
  ENABLE_APPLE_PAY_PAYMENTS: defineBooleanFlag('enable-apple-pay-payments'),
  ENABLE_GOOGLE_PAY_PAYMENTS: defineBooleanFlag('enable-google-pay-payments'),
  ENABLE_PAYPAL_PAYMENTS_IN_ONLINE_JOINING: defineBooleanFlag(
    'enable-paypal-payments-in-online-joining'
  ),
  USE_STRICT_VALIDATION_ENQUIRY_TOUR: defineBooleanFlag('use-strict-validation-enquiry-tour'),
  USE_EXTENDED_BOOK_A_TOUR_FORM: defineBooleanFlag('use-extended-book-a-tour-form'),
  SHOW_IMMEDIATE_CONTACT_PREFERENCE_POPUP_IN_ONLINE_JOINING_AND_TOUR_BOOKING: defineBooleanFlag(
    'show-immediate-contact-preferences-popup-in-online-joining-and-tour-booking'
  ),
  SHOW_TAX_ID_FIELD_SPANISH_CLUBS: defineBooleanFlag('show-tax-id-field-spanish-clubs'),
  USE_NEW_RECIPROCITY_LOGIC: defineBooleanFlag('use-new-reciprocity-logic'),
  IS_ONLINE_JOINING_DATA_CAPTURE_AFTER_RATE_CARD: defineBooleanFlag(
    'is-online-joining-data-capture-after-rate-card'
  ),
  DIGITAL_JOINING_SHOW_GENDER_FIELD_IN_IN_CLUB_JOINING: defineBooleanFlag(
    'digital-joining-show-gender-field-in-in-club-joining'
  ),
  DIGITAL_JOINING_SHOW_GENDER_FIELD_IN_ONLINE_JOINING: defineBooleanFlag(
    'digital-joining-show-gender-field-in-online-joining'
  ),
  DISABLE_ONLINE_JOINING: defineBooleanFlag('disable-online-joining'),
  DISABLE_IN_IN_CLUB_JOINING: defineBooleanFlag('disable-in-club-joining'),
  USE_NEW_CRM: defineBooleanFlag('use-new-crm'),
  USE_REDESIGN: defineBooleanFlag('use-redesign'),
  LOQATE_ADDRESS_VALIDATION_ENABLED: defineBooleanFlag('loqate-address-validation-enabled'),
  SALES_TABLET_QR_CODE_EXPIRY_DURATION: defineNumberFlag(
    'sales-tablet-qr-code-expiry-duration',
    3600
  ),
  DIGITAL_SIGNATURE_LEVEL: defineStringFlag<DigitalSignatureLevel>(
    'digital-signature-level',
    DigitalSignatureLevel.OFF
  ),
  // This default is not used by useNoMembershipsAvailableMessage, which manages its own state.
  NO_MEMBERSHIPS_AVAILABLE_MESSAGE: defineStringFlag<NoMembershipsAvailableMessage>(
    'no-membership-packages-available-message',
    NoMembershipsAvailableMessage.TEMPORARILY_UNAVAILABLE
  ),
} as const;

type FeatureFlagResponse = {
  enabled: boolean;
};

type LaunchDarklyFeatureFlagResponse<T> = {
  featureFlagId: string;
  value: T;
};

export const featureFlagKeys = {
  all: ['feature-flag'] as const,
  global: <T>(flag: FeatureFlag<T>) => [...featureFlagKeys.all, 'global', flag.key] as const,
  atClub: <T>(siteId: string, flag: FeatureFlag<T>) =>
    [...featureFlagKeys.all, 'club', siteId, flag.key] as const,
};

export const getFeatureFlag = <T>(flag: FeatureFlag<T>): Promise<T> =>
  flag.kind === 'boolean'
    ? (get<FeatureFlagResponse>(`feature-flag/${flag.key}`).then(
        (response) => response.enabled
      ) as Promise<T>)
    : get<LaunchDarklyFeatureFlagResponse<T>>(`feature-flag/ld/${flag.kind}/${flag.key}`).then(
        (response) => response.value
      );

export const getFeatureFlagAtClub = <T>(siteId: string, flag: FeatureFlag<T>): Promise<T> =>
  flag.kind === 'boolean'
    ? (get<FeatureFlagResponse>(`clubs/${siteId}/feature-flag/${flag.key}`).then(
        (response) => response.enabled
      ) as Promise<T>)
    : get<LaunchDarklyFeatureFlagResponse<T>>(
        `clubs/${siteId}/feature-flag/ld/${flag.kind}/${flag.key}`
      ).then((response) => response.value);

export function useFeatureFlag<T>(flag: FeatureFlag<T>): T {
  const [value, setValue] = useState<T>(flag.defaultValue);

  useEffect(() => {
    getFeatureFlag<T>(flag)
      .then(setValue)
      .catch(() => {
        setValue(flag.defaultValue);
      });
  }, [flag]);

  return value;
}

export function useFeatureFlagAtClub<T>(siteId: string, flag: FeatureFlag<T>): T {
  const [value, setValue] = useState<T>(flag.defaultValue);

  useEffect(() => {
    getFeatureFlagAtClub<T>(siteId, flag)
      .then(setValue)
      .catch(() => {
        setValue(flag.defaultValue);
      });
  }, [siteId, flag]);

  return value;
}

export function useSuspenseFeatureFlag<T>(flag: FeatureFlag<T>): T {
  const { data } = useSuspenseQuery({
    queryKey: featureFlagKeys.global(flag),
    queryFn: () => getFeatureFlag(flag),
  });

  return data;
}

export function useSuspenseFeatureFlagAtClub<T>(siteId: string, flag: FeatureFlag<T>): T {
  const { data } = useSuspenseQuery({
    queryKey: featureFlagKeys.atClub(siteId, flag),
    queryFn: () => getFeatureFlagAtClub(siteId, flag),
  });

  return data;
}

export function useFeatureFlagOnPage<T>(flag: FeatureFlag<T>): T {
  const { isGlobalPage, siteId } = use(SiteContext);
  const [value, setValue] = useState<T>(flag.defaultValue);

  useEffect(() => {
    const flagPromise = isGlobalPage
      ? getFeatureFlag<T>(flag)
      : getFeatureFlagAtClub<T>(siteId, flag);
    flagPromise.then(setValue).catch(() => {
      setValue(flag.defaultValue);
    });
  }, [flag, isGlobalPage, siteId]);

  return value;
}
